import { useEffect, useState } from 'react'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import { PageHeader } from '../../components/PageHeader'
import {
  fetchLivingBook, figureSlots, type BookSection, type LivingBook,
} from './rulesApi'
import { useI18n, useT } from '../../i18n'

interface Props {
  slug: string
  title: string
  jsonUrl: string
  locales: string[]
  accent?: string
  onBack: () => void
}

/**
 * Figure URLs are stable even when the image behind them is re-cut
 * (the site re-exported 73 figures at full resolution without renaming
 * them). The book JSON's width/height changes with every re-cut, so
 * keying a query param on the dimensions busts HTTP caches exactly
 * when the pixels actually changed — and never otherwise.
 */
function figSrc(fig: { url: string; width?: number; height?: number }): string {
  if (!fig.width || !fig.height) return fig.url
  const sep = fig.url.includes('?') ? '&' : '?'
  return `${fig.url}${sep}v=${fig.width}x${fig.height}`
}

/**
 * An FAQ entry arrives as ONE paragraph carrying both halves,
 * "Question?: Answer.", and the question set bold is what makes the
 * list scannable instead of a wall of prose.
 *
 * Non-greedy to the FIRST question mark, ASCII or full-width: the
 * Japanese books set ？ and the French ones put a space before ?.
 * Gated on the section id rather than run over every paragraph,
 * because ordinary rules prose is full of colons that would
 * false-positive. Verified against all 1,194 live entries in six
 * languages: every one split, none missed. The longest questions are
 * genuine multi-part ones (Coin Laundry's French runs 357 characters),
 * so there is deliberately no length cap.
 */
const FAQ_QA = /^(.*?[?？])\s*[:：]\s+/
const FAQ_SECTIONS = new Set(['faq', 'front-desk-questions'])

/** Stat-band keys in print order; anything unknown renders after these. */
const META_ORDER = ['players', 'age', 'time', 'designer', 'gameType', 'gamesRequired']
const META_LABEL: Record<string, string> = {
  players: 'PLAYERS', age: 'AGE', time: 'TIME',
  designer: 'DESIGNER', gameType: 'TYPE', gamesRequired: 'REQUIRES',
}

/**
 * Living rulebook reader — the structured, reflowing edition of a
 * printed book, in the reader's language. Renders sections in array
 * order (that IS reading order), joins figures to prose via the
 * English block index (scaled for translations), and labels the
 * whole-book English fallback when a locale doesn't exist yet.
 */
export function LivingRulebookScreen({ title, jsonUrl, locales, accent = '#111111', onBack }: Props) {
  const { lang, t } = useI18n()
  // A reader who set the app to German expects a rulebook to open in
  // German. `picked` is their per-book override from the pill row;
  // null means follow the app language. Derived rather than seeded into
  // state, so changing the app language re-selects here instead of
  // staying frozen at whatever the first mount chose.
  //
  // The rules API takes the same lowercase ISO codes as our own Lang
  // union, so `lang` passes straight through. It answers a supported
  // code that a given book lacks with the ENGLISH book and
  // `fallback: true`, which the notice below surfaces. (An UNsupported
  // code is a hard 400, which is why nothing outside Lang is ever sent.)
  const [picked, setPicked] = useState<string | null>(null)
  const locale = picked ?? lang
  const [book, setBook] = useState<LivingBook | null>(null)
  const [enBook, setEnBook] = useState<LivingBook | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setBook(null)
    setError(null)
    const wanted = fetchLivingBook(jsonUrl, locale)
    // The English edition rides along for figure placement (`after` is
    // an English block index). Skip the second request when English IS
    // the request.
    const english = locale === 'en' ? wanted : fetchLivingBook(jsonUrl, 'en').catch(() => null)
    Promise.all([wanted, english])
      .then(([b, en]) => { if (!cancelled) { setBook(b); setEnBook(en) } })
      .catch(e => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [jsonUrl, locale, attempt])

  return (
    <div className="screen">
      <BackgroundTitle text={title} color={accent} />
      <PageHeader title={title} subtitle={t('Living rulebook')} onBack={onBack} />

      {/* overflow-x-hidden: a 103-char citation URL in Tsukiji's book
          once forced the whole page into horizontal scroll — cards can
          never widen the page again, and wrap-break-word below wraps the
          long token itself. */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pb-10 relative z-10">
        {/* Language picker — whole-book switch, never per-section. */}
        {locales.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {locales.map(loc => (
              <button
                key={loc}
                type="button"
                onClick={() => setPicked(loc)}
                className={`text-xs font-display font-bold uppercase rounded-full px-3 py-1.5
                            border transition-colors
                            ${loc === locale
                              ? 'text-white border-transparent'
                              : 'bg-white text-brand-text border-brand-border'}`}
                style={loc === locale ? { backgroundColor: accent } : undefined}
              >
                {loc}
              </button>
            ))}
          </div>
        )}

        {book?.fallback && (
          <div className="card p-3 mb-3 border border-brand-border">
            <p className="text-xs text-brand-muted">{t('This language isn’t available yet. Showing the English edition.')}</p>
          </div>
        )}

        {error && (
          <div className="card p-4">
            <p className="text-sm text-brand-muted mb-3">{t('The rulebook couldn’t load. Check your connection and try again.')}</p>
            <button type="button" className="btn-primary w-full"
                    onClick={() => { setError(null); setAttempt(a => a + 1) }}>
              {t('Retry')}
            </button>
          </div>
        )}

        {!book && !error && (
          <p className="text-sm text-brand-muted py-6 text-center">{t('Loading rulebook…')}</p>
        )}

        {book?.sections.map(section => (
          <SectionCard
            key={section.id}
            section={section}
            enBlockCount={
              enBook?.sections.find(s => s.id === section.id)?.blocks.length ?? null
            }
            accent={accent}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * One prose paragraph. Inside an FAQ section it splits into a bold
 * question and its answer; anywhere else, and for any FAQ entry that
 * doesn't carry the pattern, it stays a plain run.
 */
function Paragraph({ text, faq }: { text: string; faq: boolean }) {
  const cls = 'text-sm text-brand-text leading-relaxed wrap-break-word mb-2'
  const m = faq ? FAQ_QA.exec(text) : null
  if (!m) return <p className={cls}>{text}</p>
  return (
    <p className={cls}>
      <span className="font-semibold">{m[1]}</span>{' '}
      {text.slice(m[0].length)}
    </p>
  )
}

function SectionCard({ section, enBlockCount, accent }: {
  section: BookSection
  enBlockCount: number | null
  accent: string
}) {
  const t = useT()
  const isFaq = FAQ_SECTIONS.has(section.id)
  const slots = figureSlots(section, enBlockCount)
  const meta = section.meta ?? {}
  const metaKeys = [
    ...META_ORDER.filter(k => meta[k] != null),
    ...Object.keys(meta).filter(k => !META_ORDER.includes(k)),
  ]

  return (
    <section className="card p-4 mb-3">
      {/* Printed name, uppercase, never translated (except CONTENTS/CREDITS,
          which arrive already translated from the API). */}
      <h2 className="font-display font-black uppercase tracking-wide leading-tight mb-1"
          style={{ color: accent }}>
        {section.heading}
      </h2>

      {metaKeys.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {metaKeys.map(k => (
            <span key={k}
              className="text-[10px] font-display font-bold uppercase tracking-wide
                         bg-brand-elevated text-brand-muted border border-brand-border
                         rounded-full px-2 py-0.5">
              {t(META_LABEL[k] ?? k.toUpperCase())} · {meta[k]}
            </span>
          ))}
        </div>
      )}

      {section.blocks.map((block, i) => (
        <div key={i}>
          {block.type === 'p' && (
            <Paragraph text={block.text ?? ''} faq={isFaq} />
          )}
          {block.type === 'list' && (
            <ul className="list-disc pl-5 mb-2">
              {(block.items ?? []).map((item, j) => (
                <li key={j} className="text-sm text-brand-text leading-relaxed wrap-break-word mb-1">{item}</li>
              ))}
            </ul>
          )}
          {(slots.get(i) ?? []).map(fig => (
            <img
              key={fig.url}
              src={figSrc(fig)}
              alt=""
              loading="lazy"
              className="w-full rounded-xl border border-brand-border/60 mb-2"
              style={fig.width && fig.height
                ? { aspectRatio: `${fig.width} / ${fig.height}` }
                : undefined}
            />
          ))}
        </div>
      ))}

      {/* Figures for an empty-block section (cover art etc.) */}
      {section.blocks.length === 0 && (section.figures ?? []).map(fig => (
        <img key={fig.url} src={figSrc(fig)} alt="" loading="lazy"
             className="w-full rounded-xl border border-brand-border/60 mb-2"
             style={fig.width && fig.height
               ? { aspectRatio: `${fig.width} / ${fig.height}` }
               : undefined} />
      ))}
    </section>
  )
}
